import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BlockUi from 'react-block-ui';
import Api from '../../api/global.service';
import CardContainer from '../../components/card/card-container.jsx';
import TabsNav from '../../components/link/tabs-nav.jsx';
import { useAuth } from '../../components/content/auth.jsx';
import { Colors, ChartColors } from '../../constants/Colors.js';
import UsoSirioKpis from '../../components/dashboards/uso-sirio/uso-sirio-kpis.jsx';
import UsoSirioDistribucion from '../../components/dashboards/uso-sirio/uso-sirio-distribucion.jsx';
import UsoSirioBarras from '../../components/dashboards/uso-sirio/uso-sirio-barras.jsx';
import UsoSirioTabla from '../../components/dashboards/uso-sirio/uso-sirio-tabla.jsx';
import UsoSirioHeatmap from '../../components/dashboards/uso-sirio/uso-sirio-heatmap.jsx';
import UsoSirioAcopio from '../../components/dashboards/uso-sirio/uso-sirio-acopio.jsx';
import UsoSirioUsuarios from '../../components/dashboards/uso-sirio/uso-sirio-usuarios.jsx';

const MODULOS = [
  { key: 'sic', label: 'SIC' },
  { key: 'acopio', label: 'Acopio' },
  { key: 'evaluaciones', label: 'Evaluaciones' },
  { key: 'altas', label: 'Altas' },
  { key: 'bajas', label: 'Bajas' },
];

const COLUMNAS_MODULOS = [
  { key: 'cooperativa', label: 'Cooperativa', tipo: 'texto' },
  ...MODULOS.map(modulo => ({
    key: modulo.key,
    label: modulo.label,
    tipo: 'numero',
  })),
  { key: 'total', label: 'Total', tipo: 'numero' },
  { key: 'uso_sirio', label: 'Uso de Sirio', tipo: 'estado' },
];

// Cortes fijos del mapa de calor: el mismo color significa lo mismo entre ciclos.
const CORTES_FICHAS = [4, 14, 39, 99];
const CORTES_PORCENTAJE = [20, 40, 60, 80];

const TABS_VALIDAS = ['actual', 'avanceSic', 'avanceSicPct', 'acopio', 'usuarios'];

const CooperativasUsoSirio = () => {
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();
  const tienePermiso = auth.hasAccess('COOPERATIVAS');

  // La pestaña y el ciclo viven en la URL para poder compartir una vista concreta
  const parametros = new URLSearchParams(location.search);
  const tabDeUrl = parametros.get('tab');
  const cicloDeUrl = Number(parametros.get('ciclo')) || null;

  const [ciclos, setCiclos] = useState([]);
  const [idCiclo, setIdCiclo] = useState(cicloDeUrl);
  const [tab, setTab] = useState(TABS_VALIDAS.includes(tabDeUrl) ? tabDeUrl : 'actual');

  const actualizarUrl = (nuevaTab, nuevoCiclo) => {
    const query = new URLSearchParams(location.search);
    query.set('tab', nuevaTab);
    query.set('ciclo', String(nuevoCiclo));
    history.replace({ search: `?${query.toString()}` });
  };

  const cambiarTab = nuevaTab => {
    setTab(nuevaTab);
    actualizarUrl(nuevaTab, idCiclo);
  };

  const cambiarCiclo = nuevoCiclo => {
    setIdCiclo(nuevoCiclo);
    actualizarUrl(tab, nuevoCiclo);
  };

  const [resumen, setResumen] = useState(null);
  const [avance, setAvance] = useState(null);
  const [acopio, setAcopio] = useState(null);
  const [usuarios, setUsuarios] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  // Grupo de datos que necesita cada pestaña; se piden sólo al abrirla
  const grupoDeTab = tabKey => {
    if (tabKey === 'actual') return 'resumen';
    if (['avanceSic', 'avanceSicPct'].includes(tabKey)) return 'avance';
    return tabKey;
  };

  useEffect(() => {
    let cancelado = false;
    Api.getAll('dashboards/uso-sirio/ciclos')
      .then(respuesta => {
        if (cancelado) return;
        const lista = respuesta.data || [];
        setCiclos(lista);
        // El de la URL manda; si no viene o no existe, se abre el más reciente
        setIdCiclo(actual => (lista.some(c => c.id === actual) ? actual : lista[lista.length - 1]?.id ?? null));
      })
      .catch(() => !cancelado && setError('No se pudieron cargar los ciclos.'));
    return () => {
      cancelado = true;
    };
  }, []);

  const cargar = useCallback(
    async grupo => {
      if (grupo === 'usuarios') {
        if (usuarios) return;
      } else if (!idCiclo) {
        return;
      }

      setCargando(true);
      setError(null);
      try {
        if (grupo === 'resumen') {
          const respuesta = await Api.getAll('dashboards/uso-sirio/resumen', {
            id_ciclo: idCiclo,
          });
          setResumen(respuesta.data);
        } else if (grupo === 'avance') {
          const respuesta = await Api.getAll('dashboards/uso-sirio/avance-sic', { id_ciclo: idCiclo });
          setAvance(respuesta.data);
        } else if (grupo === 'acopio') {
          const respuesta = await Api.getAll('dashboards/uso-sirio/acopio', {
            id_ciclo: idCiclo,
          });
          setAcopio(respuesta.data);
        } else if (grupo === 'usuarios') {
          const respuesta = await Api.getAll('dashboards/uso-sirio/usuarios');
          setUsuarios(respuesta.data);
        }
      } catch (e) {
        setError(e?.response?.data?.error || 'No se pudo cargar la información del tablero.');
      } finally {
        setCargando(false);
      }
    },
    [idCiclo, usuarios]
  );

  // Al cambiar de ciclo se invalida lo ya cargado (usuarios no depende del ciclo)
  useEffect(() => {
    if (!idCiclo) return;
    setResumen(null);
    setAvance(null);
    setAcopio(null);
    cargar(grupoDeTab(tab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCiclo]);

  useEffect(() => {
    cargar(grupoDeTab(tab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Se derivan del catálogo y no de la respuesta, para rotular las pestañas
  // aunque el resumen todavía no se haya pedido.
  const ciclo = ciclos.find(c => c.id === idCiclo);

  const tabs = useMemo(
    () => [
      { key: 'actual', label: 'Ciclo seleccionado', sublabel: ciclo?.nombre },
      { key: 'avanceSic', label: 'Avance SIC', sublabel: 'Fichas por semana' },
      { key: 'avanceSicPct', label: 'Avance SIC %', sublabel: '% del padrón' },
      { key: 'acopio', label: 'Acopio', sublabel: 'Desglose de registros' },
      { key: 'usuarios', label: 'Usuarios', sublabel: 'Por cooperativa y rol' },
    ],
    [ciclo]
  );

  const filasResumen = resumen?.reportes || [];

  const renderResumen = () => {
    if (!resumen) return null;

    const alto = filasResumen.filter(fila => fila.uso_sirio === 'alto').length;
    const nulo = filasResumen.filter(fila => fila.uso_sirio === 'nulo').length;
    const registros = filasResumen.reduce((acc, fila) => acc + fila.total, 0);

    return (
      <>
        <UsoSirioKpis
          items={[
            { label: 'Cooperativas activas', value: resumen.totalCooperativas },
            {
              label: 'Con uso alto',
              value: alto,
              hint: '2 o más módulos',
              color: ChartColors.estado.alto,
            },
            {
              label: 'Sin uso',
              value: nulo,
              hint: 'ningún módulo alcanza el umbral',
              color: ChartColors.estado.nulo,
            },
            {
              label: 'Registros capturados',
              value: registros.toLocaleString('es-MX'),
            },
            {
              label: 'Con SIC habilitado',
              value: resumen.sicActivoCoops,
              hint: 'según su configuración',
            },
          ]}
        />

        <CardContainer style={{ marginBottom: 16 }}>
          <UsoSirioDistribucion filas={filasResumen} umbral={resumen.umbral} />
        </CardContainer>

        <CardContainer style={{ marginBottom: 16 }}>
          <h6 className="font-lato-normal-500 heading-box mb-3">Registros por módulo y cooperativa</h6>
          <UsoSirioBarras series={MODULOS} filas={filasResumen} etiquetaTotal="registros" />
        </CardContainer>

        <CardContainer>
          <h6 className="font-lato-normal-500 heading-box mb-3">Tabla completa</h6>
          <UsoSirioTabla
            columnas={COLUMNAS_MODULOS}
            filas={filasResumen}
            conFiltroEstado
            hoja="Uso de Sirio"
            nombreArchivo={`uso_sirio_${tab}_ciclo_${idCiclo}`}
          />
          {resumen.cooperativasNuevas > 0 && (
            <small className="d-block mt-3" style={{ color: Colors.neutral_400 }}>
              {resumen.cooperativasNuevas} cooperativas se dieron de alta después de que arrancó el ciclo, así que su actividad no es
              comparable con la del resto.
            </small>
          )}
        </CardContainer>
      </>
    );
  };

  const renderAvanceSic = () => {
    if (!avance) return null;
    const filas = avance.fichas || [];
    const totalFichas = filas.reduce((acc, fila) => acc + fila.total, 0);
    const conActividad = filas.filter(fila => fila.total > 0).length;

    return (
      <>
        <UsoSirioKpis
          items={[
            {
              label: 'Fichas SIC en el ciclo',
              value: totalFichas.toLocaleString('es-MX'),
            },
            {
              label: 'Cooperativas levantando',
              value: conActividad,
              hint: `de ${filas.length}`,
            },
            { label: 'Semanas con actividad', value: avance.semanas.length },
          ]}
        />
        <CardContainer>
          <h6 className="font-lato-normal-500 heading-box mb-1">Fichas SIC levantadas por semana</h6>
          <small className="d-block mb-3" style={{ color: Colors.neutral_400 }}>
            Por fecha de inspección en campo, no por la de sincronización con Sirio. La ventana arranca cuando el levantamiento se vuelve
            sostenido, para no arrastrar las fichas sueltas que quedaron asignadas a un ciclo viejo.
          </small>
          <UsoSirioHeatmap
            semanas={avance.semanas}
            filas={filas}
            cortes={CORTES_FICHAS}
            leyenda={{ min: 'Menos', max: 'Más' }}
            resumen={{
              header: 'Total',
              valor: fila => fila.total.toLocaleString('es-MX'),
            }}
          />
          {avance.fichasFueraDeVentana > 0 && (
            <small className="d-block mt-3" style={{ color: Colors.neutral_400 }}>
              {avance.fichasFueraDeVentana} fichas quedaron fuera de la ventana por tener fecha de inspección muy anterior al arranque del
              levantamiento.
            </small>
          )}
          {avance.fichasSinFechaValida > 0 && (
            <small className="d-block" style={{ color: Colors.neutral_400 }}>
              {avance.fichasSinFechaValida} fichas no se pudieron ubicar en una semana porque su fecha de inspección está vacía, en el
              futuro o es posterior a la sincronización.
            </small>
          )}
          {avance.fichasFueraDelCiclo > 0 && (
            <small className="d-block" style={{ color: Colors.neutral_400 }}>
              {avance.fichasFueraDelCiclo} fichas traen una fecha de inspección de otro año que el del ciclo al que están asignadas; parecen
              mal asignadas de ciclo.
            </small>
          )}
          {(avance.fichasSinFechaValida > 0 || avance.fichasFueraDelCiclo > 0) && (
            <small className="d-block" style={{ color: Colors.neutral_400 }}>
              Unas y otras sí cuentan para la cobertura del padrón: al productor sí se le levantó ficha.
            </small>
          )}
        </CardContainer>
      </>
    );
  };

  const renderAvanceSicPct = () => {
    if (!avance) return null;
    const filas = (avance.avance || []).map(fila => ({
      ...fila,
      counts: Object.fromEntries(
        Object.entries(fila.counts || {}).map(([semana, n]) => [semana, fila.activos ? Math.round((n / fila.activos) * 100) : 0])
      ),
      total: fila.activos ? Math.round((fila.cobertura / fila.activos) * 100) : 0,
    }));

    const conPadron = filas.filter(fila => fila.activos > 0);
    const activos = conPadron.reduce((acc, fila) => acc + fila.activos, 0);
    const cobertura = conPadron.reduce((acc, fila) => acc + fila.cobertura, 0);

    return (
      <>
        <UsoSirioKpis
          items={[
            {
              label: 'Productores en padrón',
              value: activos.toLocaleString('es-MX'),
              hint: 'confirmados y orgánicos',
            },
            {
              label: 'Con ficha en el ciclo',
              value: cobertura.toLocaleString('es-MX'),
            },
            {
              label: 'Cobertura del padrón',
              value: activos ? `${Math.round((cobertura / activos) * 100)}%` : '—',
            },
            {
              label: 'Cooperativas sin padrón',
              value: filas.length - conPadron.length,
              hint: 'sin registro en cuentas_ciclos',
            },
          ]}
        />
        <CardContainer>
          <h6 className="font-lato-normal-500 heading-box mb-1">% de productores del padrón con ficha SIC por semana</h6>
          <small className="d-block mb-3" style={{ color: Colors.neutral_400 }}>
            Por fecha de inspección en campo. Cada productor cuenta una sola vez por semana aunque levante varias fichas. La columna
            Cobertura es el acumulado del ciclo — no la suma de las semanas — y cuenta a todo productor con ficha, incluidas las que no
            traen una fecha de inspección utilizable.
          </small>
          <UsoSirioHeatmap
            semanas={avance.semanas}
            filas={filas}
            cortes={CORTES_PORCENTAJE}
            formato={valor => `${valor}%`}
            leyenda={{ min: '0%', max: '100%' }}
            ocultarVaciasLabel="Sólo con levantamiento"
            resumen={{
              header: 'Cobertura',
              valor: fila => (fila.activos ? `${fila.total}% · ${fila.cobertura}/${fila.activos}` : 'sin padrón'),
            }}
          />
        </CardContainer>
      </>
    );
  };

  if (!tienePermiso) return null;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-start justify-content-between mb-3" style={{ gap: 12 }}>
        <div>
          <h3 className="mb-1">Uso de Sirio por cooperativa</h3>
          <p className="mb-0 text-muted" style={{ fontSize: '0.8rem', maxWidth: 720 }}></p>
        </div>
        <div style={{ minWidth: 200 }}>
          <label className="font-weight-bold d-block mb-1" style={{ fontSize: '0.85rem' }}>
            Ciclo
          </label>
          <select className="form-control select-ciclo" value={idCiclo || ''} onChange={event => cambiarCiclo(Number(event.target.value))}>
            {ciclos.map(c => (
              <option key={c.id} value={c.id}>
                CICLO {c.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      <TabsNav tabs={tabs} active={tab} onChange={cambiarTab} />

      {error && <div className="alert alert-danger">{error}</div>}

      <BlockUi tag="div" message="" blocking={cargando} renderChildren={true}>
        {tab === 'actual' && renderResumen()}
        {tab === 'avanceSic' && renderAvanceSic()}
        {tab === 'avanceSicPct' && renderAvanceSicPct()}
        {tab === 'acopio' && acopio && <UsoSirioAcopio datos={acopio} />}
        {tab === 'usuarios' && usuarios && <UsoSirioUsuarios datos={usuarios} />}
      </BlockUi>
    </div>
  );
};

export default CooperativasUsoSirio;
